import { IconTrash } from '@tabler/icons-react'
import { ConfigurationOption } from '../../types/IntegrationTypes'
import { ChangeEvent, useEffect, useState } from 'react'
import React from 'react'
import { IconButton, Switch } from '@radix-ui/themes'
import toast from 'react-hot-toast'
import { CopyIcon } from '@radix-ui/react-icons'
import { BOTTOM_TOAST_CONFIG } from '../toast/consts'

interface IIntegrationInputProps {
  option: ConfigurationOption
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  handleRadioChange?: (value: string) => void
  available?: boolean // This is used to determine active (false) or available (true) integration
  canEdit?: boolean // This is determined based on save state
  darkModeEnabled?: boolean
}

function IntegrationInput({
  option,
  available,
  handleInputChange,
  handleRadioChange,
  canEdit = true,
  darkModeEnabled = false,
}: IIntegrationInputProps) {
  const [isEditable, setIsEditable] = useState(available || !option.secret)
  const [value, setValue] = useState(option)
  const [selectedRadio, setSelectedRadio] = useState<string | undefined>(undefined)

  function enableSecret() {
    setIsEditable(true)
    setValue((prev) => ({
      ...prev,
      value: '',
    }))
  }

  useEffect(() => {
    setValue(option)
    setIsEditable(available || !option.secret)
  }, [option])

  useEffect(() => {
    if (value.type === 'radio_input') {
      const selected =
        value.value != null && value.value !== ''
          ? value.radioOptions?.find((radio) => radio.type === 'text_input')
          : value.radioOptions?.find((radio) => radio.type === 'label')
      if (handleRadioChange && selected) {
        handleRadioChange(selected.value)
      }
      setSelectedRadio(selected ? selected.value : undefined)
    }
  }, [value])

  const changeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue((prev) => ({
      ...prev,
      value: e.target.value,
    }))
    handleInputChange(e)
  }

  function changeSelect(event: ChangeEvent<HTMLSelectElement>): void {
    setValue((prev) => ({
      ...prev,
      value: event.target.value,
    }))
    handleInputChange(event)
  }

  function changeRadio(value: string): void {
    setSelectedRadio(value)
    if (value && handleRadioChange) {
      handleRadioChange(value)
    }
  }

  switch (value.type) {
    case 'text_input':
      return (
        <div className="flex flex-row gap-2 items-center">
          <input
            type="text"
            hidden={option.serverValue === 'auth_header' && canEdit} // Hide the input for auth_header when canEdit is true - keep input in form to save value
            name={value.serverValue}
            value={value.value}
            placeholder={value.placeholder}
            readOnly={!isEditable || !canEdit || option.readOnly}
            className={`not-form-styled h-8 p-2 w-full border dark:bg-inputBackground border-mainBorder dark:border-neutral-11 dark:[color-scheme:dark] rounded text-mainText dark:text-white dark:placeholder-secondaryText text-sm 
              ${!isEditable || !canEdit || option.readOnly ? 'bg-gray-100' : 'focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-1 focus:ring-borderPurple'}
              ${(!isEditable || !canEdit) && !option.readOnly && 'cursor-not-allowed'}`}
            onChange={changeValue}
          />
          {/* Additional input for auth_header to display placeholder */}
          <input
            type="text"
            value="--"
            hidden={!(option.serverValue === 'auth_header' && canEdit)}
            readOnly={true}
            className={`not-form-styled h-8 p-2 w-full border dark:bg-inputBackground border-mainBorder dark:border-neutral-11 dark:[color-scheme:dark] rounded text-mainText dark:text-white dark:placeholder-secondaryText text-sm bg-gray-100`}
          />
          {!isEditable && canEdit && <IconTrash size={20} className="cursor-pointer" onClick={enableSecret} />}
          {option.readOnly && (option.serverValue !== 'auth_header' || !canEdit) && (
            <IconButton
              size="2"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(value.value || '')
                toast.success(`${value.label || ''} copied to clipboard`, BOTTOM_TOAST_CONFIG)
              }}
            >
              <CopyIcon height="16" width="16" />
            </IconButton>
          )}
        </div>
      )
    case 'select':
      return (
        <select
          name={value.serverValue}
          value={value.value || undefined}
          disabled={!isEditable || !canEdit}
          className={`h-10 w-full rounded px-2 py-2.5 text-mainText dark:bg-inputBackground dark:text-white text-controllerText not-form-styled text-sm border-mainBorder dark:border-inputBorder focus:ring-2 focus:ring-borderPurple ${
            !isEditable || !canEdit ? 'cursor-not-allowed bg-gray-100' : 'bg-white hover:border-borderPurple'
          }`}
          onChange={changeSelect}
        >
          {value.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )
    case 'toggle':
      return (
        <div className="flex items-center gap-2">
          <Switch
            size="2"
            className={`${darkModeEnabled ? 'dark' : 'light'}`}
            // Convert stored string -> boolean for the Switch
            checked={value.value === true || value.value === 'true'}
            onCheckedChange={(checked: boolean) => {
              // Convert boolean -> string for your form model
              const stringValue = checked ? 'true' : 'false'

              // Update local state
              setValue((prev) => ({
                ...prev,
                value: stringValue,
              }))

              // Send synthetic event so form data updates correctly
              const syntheticEvent = {
                target: {
                  name: option.serverValue,
                  value: stringValue,
                },
              } as unknown as React.ChangeEvent<HTMLInputElement>

              handleInputChange(syntheticEvent)
            }}
            disabled={!isEditable || !canEdit}
          />

          {/* Hidden input so this value is still in form data */}
          <input
            type="hidden"
            name={option.serverValue}
            value={value.value === true || value.value === 'true' ? 'true' : 'false'}
            readOnly
          />
        </div>
      )
    case 'radio_input':
      return (
        <div className="flex flex-col gap-5">
          {value.radioOptions?.map((radio, i) => (
            <React.Fragment key={radio.value}>
              <label className="flex gap-2 justify-start">
                <input
                  type="radio"
                  name="sharedRadio"
                  value={radio.value}
                  checked={selectedRadio == radio.value}
                  disabled={!isEditable || !canEdit}
                  onChange={() => changeRadio(radio.value)}
                  className={`focus:ring-mainPurple dark:border-gray-200 dark:bg-black h-8 w-8 mt-1 scale-50 ${!isEditable || !canEdit ? 'cursor-not-allowed bg-gray-100 checked:bg-gray-300 dark:bg-gray-600 border-1 border-gray-300 dark:border-none' : 'cursor-pointer checked:bg-mainPurple checked:bg-mainPurple'}`}
                />
                <div className="text-mainText dark:text-white text-sm flex flex-col gap-1 w-full">
                  <span className="font-semibold">{radio.label}</span>
                  {radio.subLabel && <span className="">{radio.subLabel}</span>}
                  {radio.type == 'text_input' && (
                    <input
                      type="text"
                      name={value.serverValue}
                      placeholder={radio.placeholder}
                      value={value.value}
                      onChange={changeValue}
                      disabled={!isEditable || !canEdit || selectedRadio !== radio.value}
                      className={`not-form-styled h-8 p-2 w-full border dark:bg-inputBackground border-mainBorder dark:border-neutral-11 dark:[color-scheme:dark] dark:placeholder-secondaryText rounded text-mainText dark:text-white dark:placeholder-secondaryText text-sm ${
                        !isEditable || !canEdit || selectedRadio !== radio.value
                          ? 'cursor-not-allowed bg-gray-100'
                          : 'focus:border-borderPurple focus:border-2 hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-1 focus:ring-borderPurple'
                      }`}
                      required={radio.required && selectedRadio === radio.value}
                    />
                  )}
                </div>
              </label>
              {i !== (value?.radioOptions?.length ?? 0) - 1 && (
                <hr className="border-1 border-neutral-4 dark:border-neutral-9" />
              )}
            </React.Fragment>
          ))}
        </div>
      )
    default:
      return <p>Not supported</p>
  }
}

export default IntegrationInput
